/* ============================================================
   The paths, and which of them existed before.

   The old site was eight html entries behind nginx rewrites; these are the same addresses, now owned by the
   router. The addresses themselves are a contract — they are shared, they are in people's history, and the
   OAuth apps have the two callbacks registered by URL.

   Share pages are deliberately last in the rebuild (P2) but keep their paths from day one, so a link written
   today still lands somewhere sensible.
   ============================================================ */
import type { RouteRecordRaw } from 'vue-router';
import { PHONE } from '@/utils/util.js';

/* THE PHONE'S OWN FACE (docs/phone/PHONE-PORT.md §3.1): the same addresses, another view — the front door and the room are
   drawn for a phone when util.js says PHONE (a handheld whose short side is under 600: an iPad keeps the desktop). The share
   pages, the callbacks and the legal pages are one view for every device. */
/* the front door picks its face ONCE, here, and each face is its own loader: `() => (PHONE ? import(a) : import(b))` was
   folded by the minifier into ONE preload call carrying only the desktop's list, so a phone fetched the desktop door's
   chunks (~75 KB gzip) and asked for its own a round trip late (measured 2026-09-27). The room the same way: a phone waited
   for the desktop room's imports, then its own chunk, then its thread — two trips before the empty room's shelves (owner
   2026-09-27: "推荐 vibe 的页面还是要很久才出现") */
const home = PHONE ? () => import('@/views/phone/PhoneHomeView.vue') : () => import('@/views/home/HomeView.vue');
const listen = PHONE ? () => import('@/views/phone/PhoneRoomView.vue') : () => import('@/views/room/RoomView.vue');

export const routes: RouteRecordRaw[] = [
  {
    /* `/` is two things by design: the front door for a guest, the room once she knows you. The old site did
       this with a redirect at boot; here the view decides, so the address never flickers. */
    path: '/',
    name: 'home',
    meta: { title: 'Miya: Your Personal AI Music Agent' },
    component: home,
  },
  {
    path: '/listen',
    name: 'listen',
    meta: { title: 'Miya: Your Personal AI Music Agent', auth: true },
    component: listen,
  },
  /* the two OAuth landings. Registered with X and Google BY URL — these two paths cannot be renamed.
     `bounce` keeps the guard's boot() off them: they hold the round trip's ?code for a few hundred milliseconds
     and then hand the whole address to the page that will actually finish the sign-in. Booting here SPENT the
     landing — the code was consumed, `is_new` and the front door's carry were computed, and then the bounce
     navigated away and threw all of it out (measured 2026-09-26: her greeting never came, because the room's
     `firstNight` was decided on a page that no longer existed). */
  {
    path: '/x-callback',
    /* the old page's name — what the provider (and the mocks' authorize page) sends the round trip back to */
    alias: '/x-callback.html',
    name: 'x-callback',
    meta: { title: 'Miya.FM — signing in…', bounce: true },
    component: () => import('@/views/auth/XCallbackView.vue'),
  },
  {
    path: '/google-callback',
    alias: '/google-callback.html',
    name: 'google-callback',
    meta: { title: 'Miya.FM — signing in…', bounce: true },
    component: () => import('@/views/auth/GoogleCallbackView.vue'),
  },
  /* the share pages: a visitor's way in, no account needed */
  {
    path: '/songs/:rest(.*)' /* everything nginx sent to song.html; the view reads the id */,
    name: 'song',
    meta: { title: 'A song by Miya — Miya.FM' },
    component: () => import('@/views/share/SongView.vue'),
  },
  {
    path: '/videos/:rest(.*)',
    name: 'video',
    meta: { title: 'A song by Miya — Miya.FM' },
    component: () => import('@/views/share/SongView.vue'),
    props: { video: true } /* the same view wearing another face, as body.video was */,
  },
  {
    path: '/playlists/:rest(.*)',
    name: 'playlist',
    meta: { title: 'A playlist from Miya’s room — Miya.FM' },
    component: () => import('@/views/share/PlaylistView.vue'),
  },
  /* the legal pages answer at their old names too: nginx served /terms.html and /privacy.html as files (and /terms,
     /privacy onto them), and the room's door and the front door link the .html names (door.js:53, index.html:306) */
  {
    path: '/terms',
    alias: '/terms.html',
    name: 'terms',
    meta: { title: 'Miya — Terms of Use' },
    component: () => import('@/views/legal/TermsView.vue'),
  },
  {
    path: '/privacy',
    alias: '/privacy.html',
    name: 'privacy',
    meta: { title: 'Miya — Privacy Policy' },
    component: () => import('@/views/legal/PrivacyView.vue'),
  },
  /* the DJ set builder (experiment, branch ian-dj-exp): a page of its own that signs in by itself (DjView) */
  {
    path: '/dj',
    name: 'dj',
    meta: { title: 'Miya DJ — set builder' },
    component: () => import('@/views/dj/DjView.vue'),
  },
  {
    /* AN ADDRESS NO PAGE OWNS is the front door's document, AT that address (nginx `location / { try_files $uri $uri/
       /index.html }` serves it without a redirect): index.html's door has already sent a session, a round trip or a pass
       on to the room, so what is left here is a visitor at the landing — the query and the hash where they were. */
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    meta: { title: 'Miya: Your Personal AI Music Agent' },
    component: home,
  },
];
